<!--@format-->
<template>
    <div class="social-post-select-widget">
        <div class="widget-header">
            <div class="title-container">
                <h3>{{ widgetTitle }}</h3>
            </div>
            <div class="svg-container widget-close-button" @click="closeWidget">
                <widget-close-svg />
            </div>
        </div>
        <div class="content-wrapper">
            <cards-list-widget
                :multiple="false"
                :items="postItems"
                :vertical="false"
                :loading="isLoading"
                :tooltips-text="tooltipsText"
                :no-records-content="emptyListDescription"
                :filter="isTemplatesClient"
                :filter-items="filterItems"
                @filter-by="handleFilterBy"
                @search="handleUpdateSearch"
                @show-preview="handleShowPreview"
                @input="handleInputSelect"
            />
        </div>
        <div class="widget-footer">
            <div class="cancel-container">
                <app-button class="transparent-btn" @click="closeWidget"> Cancel </app-button>
            </div>
            <div class="actions-container">
                <div class="items-counter">
                    <span v-if="selectedItem"
                        >{{ `1 ${isTemplates ? 'Template' : 'Post'} Selected` }}
                    </span>
                </div>
                <app-button :disabled="isButtonDisabled" @click="handleNext"> Next </app-button>
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/scripts/event-bus.js';
import { SocialMediaPostService } from '@/scripts/services/social-media/social-media-post-service';
import { SocialMediaPostTemplateService } from '@/scripts/services/social-media/social-media-post-template-service';
import { SocialMediaSharedAssetsService } from '@/scripts/services/social-media/social-media-shared-assets-service';

const AppButton = () => import('@/components/common/app-button');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg');
const CardsListWidget = () => import('@/components/sliding-widgets/cards-list-widget');

export default {
    name: 'social-post-select-widget',

    components: {
        'app-button': AppButton,
        'widget-close-svg': WidgetCloseSvg,
        'cards-list-widget': CardsListWidget,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data: () => {
        return {
            searchValue: '',
            filterValue: '',
            promoId: null,
            postItems: [],
            selectedItem: null,
            isLoading: false,
            totalItems: 0,
            filterItems: [
                {
                    name: 'My Templates',
                    value: 'my_templates',
                },
                {
                    name: 'Included With Subscription',
                    value: 'promo_included',
                },
                {
                    name: 'Shared Templates',
                    value: 'shared_resource',
                },
            ],
        };
    },

    computed: {
        isTemplates() {
            return this.widget.options.isTemplates;
        },

        tooltipsText() {
            return this.isTemplates
                ? {
                      close: 'Deselect Template',
                      preview: 'Preview',
                  }
                : {
                      close: 'Deselect Post',
                      preview: 'Preview',
                  };
        },

        widgetTitle() {
            return this.isTemplates ? 'Choose a Template' : 'Choose a Post';
        },

        clientId() {
            return this.widget.options.clientId;
        },

        accountId() {
            return this.widget.options.accountId;
        },

        isButtonDisabled() {
            return !this.selectedItem;
        },

        isTemplatesClient() {
            return !!(this.isTemplates && this.clientId);
        },

        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        emptyListDescription() {
            return `Please add a ${this.isTemplates ? 'template' : 'post '} to populate this table`;
        },
    },

    watch: {
        searchValue: {
            deep: true,
            handler() {
                this.getData();
            },
        },

        filterValue: {
            deep: true,
            handler() {
                this.getData();
            },
        },
    },

    mounted() {
        if (this.isTemplatesClient) {
            this.getPromoId().then(this.getData);
        } else {
            this.getData();
        }
    },

    methods: {
        getFilterParam() {
            const queryFilters = [];

            if (!this.isTemplates) {
                queryFilters.push('"Status": "posted"');
            }

            if (this.filterValue) {
                queryFilters.push(`"TemplateType": "${this.filterValue}"`);

                if (this.filterValue === 'promo_included' && this.promoId) {
                    queryFilters.push(`"PromoId": ${this.promoId}`);
                }
            }

            if (this.searchValue) {
                if (!this.isTemplates) {
                    queryFilters.push(
                        `"PostName": {"$like": "${encodeURIComponent(`%${this.searchValue}%`)}"}`,
                    );
                } else {
                    queryFilters.push(
                        `"TemplateName": {"$like": "${encodeURIComponent(`%${this.searchValue}%`)}"}`,
                    );
                }
            }

            return queryFilters.length > 0 ? `{${queryFilters.join(',')}}` : null;
        },

        asSocialPostCardItem(item) {
            return {
                id: item.Id,
                previewName: item.PostName || item.TemplateName,
                previewImage: item.MediumThumbnail,
            };
        },

        getData() {
            this.isLoading = true;

            const requestConfig = {
                filter: this.getFilterParam(),
                order: '["CreatedAt", "DESC"]',
                count: true,
            };

            if (!this.isTemplates) {
                SocialMediaPostService.getAll(requestConfig, this.clientId)
                    .then(({ data }) => {
                        this.postItems = data.data.map((item) => this.asSocialPostCardItem(item));
                        this.totalItems = data.count;
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your social posts.',
                            { error },
                        );
                        this.closeWidget();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            } else {
                SocialMediaPostTemplateService.getAll(requestConfig, this.accountId)
                    .then(({ data }) => {
                        this.postItems = data.data.map((item) => this.asSocialPostCardItem(item));
                        this.totalItems = data.count;
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while fetching your social post templates.',
                            { error },
                        );
                        this.closeWidget();
                    })
                    .finally(() => {
                        this.isLoading = false;
                    });
            }
        },

        getPromoId() {
            return new Promise((resolve, reject) => {
                SocialMediaSharedAssetsService.getClient(this.clientId, {
                    select: 'PromoId',
                })
                    .then((response) => {
                        this.promoId = response.data.value[0].PromoId;
                        resolve();
                    })
                    .catch((error) => {
                        this.$helpers.showFailNotification(
                            'An error occurred while getting your social post templates.',
                            { error },
                        );
                        reject();
                    });
            });
        },

        handleFilterBy(filter) {
            this.filterValue = filter.value;
        },

        handleUpdateSearch(search) {
            const encodedSearch = encodeURIComponent(search);

            this.searchValue = encodedSearch;
        },

        handleInputSelect(items) {
            this.selectedItem = items.length > 0 ? items[0] : null;
        },

        handleShowPreview(item) {
            const routerData = this.isTemplates
                ? this.$router.resolve({
                      path: `${this.baseUrl}/SocialMedias/PostTemplates/Template/Preview/${item.id}`,
                  })
                : this.$router.resolve({
                      path: `${this.baseUrl}/SocialMedias/SocialPosts/Post/Preview/${item.id}`,
                  });

            window.open(routerData.href);
        },

        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        handleNext() {
            if (this.selectedItem) {
                if (!this.isTemplates) {
                    EventBus.$emit('social-post-select', this.selectedItem);
                } else {
                    EventBus.$emit('post-template-select', this.selectedItem);
                }
            }

            this.closeWidget();
        },
    },
};
</script>

<style>
.social-post-select-widget.widget .content-wrapper {
    padding: 25px;
}

.social-post-select-widget .transparent-btn {
    background: transparent;
    color: #877d88;
}

.social-post-select-widget .transparent-btn:hover {
    background: #e1e0e5;
}

.social-post-select-widget .actions-container {
    display: flex;
    align-items: center;
    gap: 40px;
}

.social-post-select-widget .actions-container .items-counter {
    font-size: 14px;
    font-weight: 500;
}
</style>
